import { useEffect, useState } from "react";
import { FirestoreService } from "../../../../api/firebase/firestoreService";
import { FirestoreDocumentReference } from "../../../../api/firebase/references";
import type { PocReassignmentDoc } from "../../../../data-model/pocReassignmentRule";

/**
 * Fetches the POC reassignment Firestore doc once on mount.
 *
 * @returns data — the document contents (null when not found),
 *          loading — true while the request is in flight,
 *          error — populated when the fetch fails for a reason other than "not found".
 */
export function usePocReassignmentConfig() {
  const [data, setData] = useState<PocReassignmentDoc | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let cancelled = false;

    const fetchConfig = async () => {
      setLoading(true);
      setError(null);
      try {
        const result = await FirestoreService.getDocument<PocReassignmentDoc>(
          FirestoreDocumentReference.pharmacyOrderUpdatesPocReassignment(),
        );
        if (!cancelled) setData(result);
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        if (message.includes("does not exist")) {
          if (!cancelled) setData(null);
        } else {
          const errorInstance =
            err instanceof Error ? err : new Error(String(err));
          if (!cancelled) setError(errorInstance);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchConfig();
    return () => {
      cancelled = true;
    };
  }, []);

  return { data, loading, error };
}
